import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo } from 'react'

import type { TableHeaders, TableProps } from '@fourthwall/components'
import {
  ConfirmModal,
  Dropdown,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon, PencilIcon, TrashIcon } from '@fourthwall/icons'
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'

import { EmojiPreview } from '../../components/EmojiPreview'
import { NameCell } from '../../components/NameCell'
import { useCreateEmojiMutation } from '../../hooks/useCreateEmojiMutation'
import { useDeleteEmojiMutation } from '../../hooks/useDeleteEmojiMutation'
import { useEmojisQuery } from '../../hooks/useEmojisQuery'
import { useReorderEmojiMutation } from '../../hooks/useReorderEmojiMutation'
import { useUpdateEmojiMutation } from '../../hooks/useUpdateEmojiMutation'
import { CustomEmojiModal } from '../../modules/CustomEmojiModal/CustomEmojiModal'
import type { Emoji, TableData } from '../../types'
import { getAccessType, getEmojiAvailableTo } from './utils'

type Props = { children: ReactNode }

type CustomEmojiContextType = {
  badges?: Emoji[]
  table: TableProps
  openCreateEmojiModal: () => void
  isFetching: boolean
  isLoading: boolean
}

export const CustomEmojiContext = createContext<CustomEmojiContextType | undefined>(undefined)

export const CustomEmojiProvider = ({ children }: Props) => {
  const modal = useModalContext()
  const media = useMediaQueryContext()

  const { emojisQuery } = useEmojisQuery()
  const { tiersQuery } = useTiersQuery()

  const { createEmojiMutation } = useCreateEmojiMutation()
  const { updateEmojiMutation } = useUpdateEmojiMutation()
  const { reorderEmojiMutation } = useReorderEmojiMutation()
  const { deleteEmojiMutation } = useDeleteEmojiMutation()

  const deleteBadge = useCallback(
    (badgeId: number) => {
      modal.open(ConfirmModal, {
        title: 'Are you sure you want to delete this custom emoji?',
        confirmLabel: 'Delete',
        confirmAppearance: 'destructive',
        onConfirm: () => {
          deleteEmojiMutation.mutate([{ id: badgeId }], {
            onSuccess: emojisQuery.refetch,
          })
        },
      })
    },
    [emojisQuery, deleteEmojiMutation, modal],
  )

  const openEditEmojiModal = useCallback(
    (emoji: Emoji) => {
      modal.open(CustomEmojiModal, {
        emoji,
        onSubmit: (values, imageResourceId) => {
          return updateEmojiMutation.mutateAsync(
            [
              { id: emoji.id },
              {
                name: values.text,
                accessType: getAccessType(values),
                newImageResourceId: imageResourceId,
              },
            ],
            {
              onSuccess: () => {
                emojisQuery.refetch()
                modal.close()
              },
            },
          )
        },
        onClose: modal.close,
      })
    },
    [modal, emojisQuery, updateEmojiMutation],
  )

  const openCreateEmojiModal = useCallback(() => {
    modal.open(CustomEmojiModal, {
      onSubmit: (values, imageResourceId) => {
        return createEmojiMutation.mutateAsync(
          [
            {
              name: values.text,
              accessType: getAccessType(values),
              imageResourceId: imageResourceId!,
            },
          ],
          {
            onSuccess: () => {
              emojisQuery.refetch()
              modal.close()
            },
          },
        )
      },
      onClose: modal.close,
    })
  }, [modal, emojisQuery, createEmojiMutation])

  const tableHeaders: TableHeaders<TableData> = useMemo(
    () => [
      {
        id: 'image',
        label: 'Image',
        width: 1,
        render: (emoji) => <EmojiPreview url={emoji.imageUrl} />,
      },
      {
        id: 'name',
        label: 'Name',
        width: 3,
        render: (emoji) => <NameCell>:{emoji.name}:</NameCell>,
      },
      {
        id: 'availableTo',
        label: 'Available to',
        width: 5,
        render: (emoji) => {
          if (!tiersQuery.data) return

          return getEmojiAvailableTo(emoji, tiersQuery.data?.tiers)
        },
      },
      {
        id: 'settings',
        label: ' ',
        alignment: 'right',
        width: 2,
        render: (emoji) => (
          <Dropdown
            content={
              <>
                <Dropdown.Item
                  label="Edit"
                  leftIcon={PencilIcon}
                  onClick={() => {
                    openEditEmojiModal(emoji)
                  }}
                />
                <Dropdown.Item
                  label="Delete"
                  leftIcon={TrashIcon}
                  onClick={() => {
                    deleteBadge(emoji.id)
                  }}
                />
              </>
            }
            position="bottom-end"
          >
            <ToolbarButton icon={DotsHorizontalIcon} />
          </Dropdown>
        ),
      },
    ],
    [tiersQuery.data, deleteBadge, openEditEmojiModal],
  )

  const mobileTableHeaders: TableHeaders<TableData> = useMemo(
    () => [
      {
        id: 'image',
        label: 'Image',
        render: (emoji) => <EmojiPreview url={emoji.imageUrl} />,
        width: 2,
      },
      {
        id: 'name',
        label: 'Name',
        width: 10,
        render: (emoji) => {
          if (!tiersQuery.data) return

          return (
            <NameCell info={getEmojiAvailableTo(emoji, tiersQuery.data?.tiers)}>
              :{emoji.name}:
            </NameCell>
          )
        },
      },
      {
        id: 'settings',
        label: ' ',
        alignment: 'right',
        width: 2,
        render: (emoji) => (
          <Dropdown
            content={
              <>
                <Dropdown.Item
                  label="Edit"
                  leftIcon={PencilIcon}
                  onClick={() => {
                    openEditEmojiModal(emoji)
                  }}
                />
                <Dropdown.Item
                  label="Delete"
                  leftIcon={TrashIcon}
                  onClick={() => {
                    deleteBadge(emoji.id)
                  }}
                />
              </>
            }
            position="bottom-end"
          >
            <ToolbarButton icon={DotsHorizontalIcon} />
          </Dropdown>
        ),
      },
    ],
    [tiersQuery.data, openEditEmojiModal, deleteBadge],
  )

  const tableData: TableData[] = useMemo(() => {
    if (!emojisQuery.data) return []

    return emojisQuery.data.emojis
  }, [emojisQuery.data])

  const table: TableProps<TableData> = useMemo(
    () => ({
      data: tableData,
      headers: media.tablet ? tableHeaders : mobileTableHeaders,
      emptyMessage: 'You haven’t created any custom emoji yet.',
      minWidth: 0,
      onPositionChange: (oldIndex, newIndex) => {
        reorderEmojiMutation.mutateAsync(
          [
            {
              id: tableData[oldIndex].id,
            },
            {
              position: newIndex,
            },
          ],
          {
            onSuccess: emojisQuery.refetch,
          },
        )
      },
      sortable: media.tablet,
    }),
    [tableData, media, tableHeaders, reorderEmojiMutation, emojisQuery, mobileTableHeaders],
  )

  const value = useMemo(
    () => ({
      badges: emojisQuery.data?.emojis,
      table,
      openCreateEmojiModal,
      isFetching: !emojisQuery.data?.emojis || !tiersQuery.data,
      isLoading:
        emojisQuery.isFetching || reorderEmojiMutation.isLoading || deleteEmojiMutation.isLoading,
    }),
    [
      emojisQuery,
      tiersQuery,
      table,
      openCreateEmojiModal,
      reorderEmojiMutation.isLoading,
      deleteEmojiMutation.isLoading,
    ],
  )

  return <CustomEmojiContext.Provider value={value}>{children}</CustomEmojiContext.Provider>
}
